import { useState, useEffect, useRef, useCallback } from "react";
import { MagnifyingGlass, MapPin, X } from "@phosphor-icons/react";
import { Input } from "./ui/input";
import API from "../lib/api";
import axios from "axios";

const PUBLIC_API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * UK address autocomplete component powered by Google Places API (New).
 *
 * Props:
 *   value            - string: current displayed text (controlled)
 *   onChange         - fn(string): fires on every keystroke
 *   onSelect         - fn(structured): fires when a suggestion is picked. Structured object:
 *                      { street_address, town_city, county, postcode, latitude, longitude, formatted_address }
 *   placeholder      - string
 *   label            - string (optional): renders a label above
 *   required         - bool
 *   publicMode       - bool: use /public/address/* endpoints (no auth, IP rate-limited). For public forms.
 *   "data-testid"    - string prefix for test ids
 */
export default function AddressAutocomplete({
  value = "",
  onChange,
  onSelect,
  placeholder = "Start typing address or postcode...",
  label,
  required = false,
  publicMode = false,
  "data-testid": testId = "address-autocomplete",
  className = "",
}) {
  const [query, setQuery] = useState(value);
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [activeIdx, setActiveIdx] = useState(-1);
  const [loading, setLoading] = useState(false);
  const [fetchingDetails, setFetchingDetails] = useState(false);
  const sessionTokenRef = useRef(null);
  const debounceRef = useRef(null);
  const wrapperRef = useRef(null);

  // Keep local state in sync if parent changes value externally
  useEffect(() => { setQuery(value || ""); }, [value]);

  // Generate a new session token on mount (one session = one billable query group)
  useEffect(() => {
    sessionTokenRef.current = crypto.randomUUID();
  }, []);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const fetchSuggestions = useCallback(async (q) => {
    if (!q || q.trim().length < 3) {
      setSuggestions([]);
      return;
    }
    setLoading(true);
    try {
      const url = publicMode ? "/public/address/autocomplete" : "/address/autocomplete";
      const res = publicMode
        ? await axios.get(`${PUBLIC_API}${url}`, { params: { q, session_token: sessionTokenRef.current } })
        : await API.get(url, { params: { q, session_token: sessionTokenRef.current } });
      setSuggestions(res.data?.suggestions || []);
      setOpen(true);
      setActiveIdx(-1);
    } catch {
      setSuggestions([]);
    } finally {
      setLoading(false);
    }
  }, [publicMode]);

  const handleInputChange = (e) => {
    const v = e.target.value;
    setQuery(v);
    onChange?.(v);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchSuggestions(v), 250);
  };

  const handlePick = async (s) => {
    setOpen(false);
    setSuggestions([]);
    setFetchingDetails(true);
    try {
      const url = publicMode
        ? `/public/address/details/${encodeURIComponent(s.place_id)}`
        : `/address/details/${encodeURIComponent(s.place_id)}`;
      const res = publicMode
        ? await axios.get(`${PUBLIC_API}${url}`, { params: { session_token: sessionTokenRef.current } })
        : await API.get(url, { params: { session_token: sessionTokenRef.current } });
      const data = res.data;
      const display = data.formatted_address || s.description;
      setQuery(display);
      onChange?.(display);
      onSelect?.(data);
      // Rotate the session token — one session ends on selection
      sessionTokenRef.current = crypto.randomUUID();
    } catch {
      // If details fail, fall back to the description
      setQuery(s.description);
      onChange?.(s.description);
    } finally {
      setFetchingDetails(false);
    }
  };

  const handleKeyDown = (e) => {
    if (!open || suggestions.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIdx(i => Math.min(i + 1, suggestions.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIdx(i => Math.max(i - 1, 0));
    } else if (e.key === "Enter" && activeIdx >= 0) {
      e.preventDefault();
      handlePick(suggestions[activeIdx]);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  return (
    <div className={`relative ${className}`} ref={wrapperRef} data-testid={testId}>
      {label && (
        <label className="text-xs font-medium mb-1 block">
          {label}{required && <span className="text-red-500 ml-0.5">*</span>}
        </label>
      )}
      <div className="relative">
        <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
        <Input
          type="text"
          value={query}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => { if (suggestions.length > 0) setOpen(true); }}
          placeholder={placeholder}
          required={required}
          className="pl-8 pr-8"
          autoComplete="off"
          data-testid={`${testId}-input`}
        />
        {(loading || fetchingDetails) && (
          <div className="absolute right-2.5 top-1/2 -translate-y-1/2">
            <div className="h-3 w-3 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        )}
        {!loading && !fetchingDetails && query && (
          <button
            type="button"
            onClick={() => { setQuery(""); onChange?.(""); setSuggestions([]); setOpen(false); }}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            data-testid={`${testId}-clear`}
            tabIndex={-1}
          >
            <X size={12} />
          </button>
        )}
      </div>

      {open && suggestions.length > 0 && (
        <div
          className="absolute z-50 mt-1 w-full max-h-72 overflow-auto rounded-md border border-border bg-popover shadow-lg"
          data-testid={`${testId}-dropdown`}
        >
          {suggestions.map((s, idx) => (
            <button
              type="button"
              key={s.place_id}
              onClick={() => handlePick(s)}
              onMouseEnter={() => setActiveIdx(idx)}
              className={`w-full text-left px-3 py-2 flex items-start gap-2 border-b border-border last:border-b-0 transition-colors ${
                idx === activeIdx ? "bg-accent" : "hover:bg-accent/50"
              }`}
              data-testid={`${testId}-suggestion-${idx}`}
            >
              <MapPin size={14} className="text-primary mt-0.5 shrink-0" weight="duotone" />
              <div className="min-w-0">
                <div className="text-sm font-medium truncate">{s.main_text}</div>
                <div className="text-xs text-muted-foreground truncate">{s.secondary_text}</div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
